/* global $ */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { decodePassengers } from 'utils/Reserbus';
import {
  productListTracker,
  resultsActionsTracker,
  sortTracker,
  filterTracker,
} from 'metrics/user-analytics/search';
import pickedProviderTracker from 'metrics/user-analytics/pickedProviderTracker';
import { selectedTripTracker } from 'metrics/user-analytics/trip';
import LoadingScreen from 'components/LoadingScreen';
import TripListHeader from 'components/TripListHeader';
import TripList from 'components/search/TripList';
import DiscountUITracker from 'components/search/DiscountUITracker';
import PromoUrgencyBannerContainer from 'components/search/PromoUrgencyBannerContainer';
import parseQueryString from 'utils/queryString/parseQueryString';
import TripDetails from 'components/TripDetails';
import OverlayView from 'components/OverlayView';
import { Spacing, Text, Visibility } from '@reservamos/elements';
import ModalDiscount from 'components/search/ModalContent/ModalDiscount';
import ModalOpenTicket from 'components/search/ModalContent/ModalOpenTicket';
import { getMomentFromSearchDateParam } from 'utils/search/searchDateParam';
import normalizeSearchWidgetDateFormat from 'utils/normalizeSearchWidgetDateFormat';
import DiscountBanner from 'ui/atoms/DiscountBanner';
import SwitchDoters from 'components/search/SwitchDoters/SwitchDoters';
import LineBanner from 'ui/atoms/LineBanner';
import { When } from 'react-if';
import RouteTerminalsDisplay from 'ui/molecules/RouteTerminalsDisplay';
import { getRecommendedRedirectPayload, trackRecommendedTripSelected } from 'utils/userPreferences';
import { getSeatsPurchasePayload, isSeatsOnResultActivated } from 'utils/seats';
import PoweredBy from 'components/PoweredBy';
import DaysCarousel from 'components/search/DaysCarousel';
import NoResults from '../../../ui/molecules/NoResults';
import BenefitDenialModal from '../BenefitDenialModal/BenefitDenialModal';
import SearchSidebar from '../../SearchSidebar';
import LabelLoading from '../../../ui/molecules/LabelLoading';
import NormalTicketWrapper from '../NormalTicketWrapper';
import NormalTicketButton from '../NormalTicketButton';
import Coupon from '../SearchCoupon/SearchCoupon';
import OpenTicketList from '../OpenTicketList';
import ChangeProvider from '../../../ui/atoms/ChangeProvider';
import DayControls from '../DayControls';
import WhiteBar from '../../../ui/atoms/WhiteBar';
import NerbyTerminals from '../NerbyTerminals';
import ResultsTitle from '../../../ui/atoms/ResultsTitle';
import { cleanAnalyticsParams } from '../../../utils/urls';
import RecommendedTripsList from '../RecommendedTrips';
import OpenTicketCTA from '../OpenTicketCTA';
import ClosedTripsSection from '../ClosedTripsSection';
import QuickPurchaseModal from '../QuickPurchaseModal';
import QuickPurchaseIntroModal from '../QuickPurchaseIntroModal';
import useQuickPurchaseStatusTracker from '../../../hooks/useQuickPurchaseStatusTracker';
import useQuickPurchaseGBAttribute from '../../../hooks/useQuickPurchaseGBAttribute';
import { unlockSeats } from '../../../actions/purchase';

/**
 * QuickPurchaseExperimentGate component.
 * Sets the `isEligibleForQuickPurchase` attribute on GrowthBook and gates the
 * render of its children until the attribute has been applied. Ensures consumers
 * of the QUICK_PURCHASE_ENABLED experiment value see the targeted variant on
 * their first render (no flicker between default and targeted value). Also fires
 * the "Quick Purchase Status" Mixpanel event once per search results view.
 * @param {object} props - Component props.
 * @param {Array} props.trips - Search result trips.
 * @param {boolean} props.isRoundTrip - Whether the search is a round trip.
 * @param {boolean} props.isExchange - Whether the flow is an exchange.
 * @param {boolean} props.isSearchPolling - Whether the search is still polling.
 * @param {boolean} props.noTrips - Whether the search resolved with zero trips.
 * @param {React.ReactNode} props.children - Content rendered once GrowthBook is ready.
 * @returns {React.ReactNode}
 */
const QuickPurchaseExperimentGate = ({
  trips,
  isRoundTrip,
  isExchange,
  isSearchPolling,
  children,
}) => {
  const {
    isReady,
    isEligibleForQuickPurchase,
    anyTripAllowsSeatSelection,
    hasEnoughPassengers,
    qpPassengers,
    passengersSource,
  } = useQuickPurchaseGBAttribute({
    trips,
    isRoundTrip,
    isExchange,
    isSearchPolling,
  });

  useQuickPurchaseStatusTracker({
    isReady,
    isEligibleForQuickPurchase,
    anyTripAllowsSeatSelection,
    hasEnoughPassengers,
    isRoundTrip,
    qpPassengers,
    passengersSource,
    trips,
  });

  if (!isReady) return <LoadingScreen resultMessages hasHeader={false} />;
  return children;
};

QuickPurchaseExperimentGate.propTypes = {
  trips: PropTypes.array.isRequired,
  isRoundTrip: PropTypes.bool.isRequired,
  isExchange: PropTypes.bool.isRequired,
  isSearchPolling: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

const GENERAL_PASSENGER_TYPES = ['adult', 'general', 'child_in_arms'];

/**
 * Maps a full-gratuity benefit category to its half-fare (50%) equivalent. Used to detect when the
 * 100% free slot (e.g. idoso) is sold out but the paid 50% fare is still available, so the
 * benefit-denial modal can offer buying at 50% as an alternative to issuing the denial.
 */
const HALF_FARE_BY_CATEGORY = {
  older: 'older_half',
  disadvantaged_youth: 'disadvantaged_youth_half',
};

/**
 * Get the first selected non-general passenger category.
 * @param {string} encodedPassengers - Encoded passengers string from URL.
 * @returns {string|undefined} The first non-general passenger type with count > 0, or undefined.
 */
const getSelectedPassengerCategory = (encodedPassengers) => {
  const passengers = decodePassengers(encodedPassengers);

  return Object.keys(passengers).find(
    (passengerType) =>
      !GENERAL_PASSENGER_TYPES.includes(passengerType) && passengers[passengerType] > 0,
  );
};

const propTypes = {
  origin: PropTypes.object.isRequired,
  destination: PropTypes.object.isRequired,
  passengers: PropTypes.string.isRequired,
  roundTrip: PropTypes.bool.isRequired,
  departureDate: PropTypes.string.isRequired,
  returnDate: PropTypes.string,
  providers: PropTypes.array,
  provider: PropTypes.object,
  transportType: PropTypes.string,
  searchIsPolling: PropTypes.bool.isRequired,
  paymentPlans: PropTypes.objectOf(PropTypes.number).isRequired,
  installmentsMinAmount: PropTypes.number.isRequired,
  trips: PropTypes.array.isRequired,
  promoUrgencyTrips: PropTypes.array.isRequired,
  closedTrips: PropTypes.array,
  noTrips: PropTypes.bool.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortProvidersBy: PropTypes.string,
  departureFilter: PropTypes.string.isRequired,
  stopsFilter: PropTypes.string.isRequired,
  locations: PropTypes.object.isRequired,
  departureLocationFilter: PropTypes.string.isRequired,
  arrivalLocationFilter: PropTypes.string.isRequired,
  activeDepartureFilter: PropTypes.bool.isRequired,
  activeLocationFilter: PropTypes.bool.isRequired,
  activeStopsFilter: PropTypes.bool.isRequired,
  tooManyFilters: PropTypes.bool.isRequired,
  currentPath: PropTypes.string.isRequired,
  providerName: PropTypes.string,
  adAttribution: PropTypes.string,
  showModal: PropTypes.func.isRequired,
  setSortOrder: PropTypes.func.isRequired,
  setTripFilter: PropTypes.func.isRequired,
  resetTripFilters: PropTypes.func.isRequired,
  transitionTo: PropTypes.func.isRequired,
  replaceUrl: PropTypes.func.isRequired,
  closeTripDetails: PropTypes.func.isRequired,
  tripDetails: PropTypes.object,
  openTrip: PropTypes.object,
  isStopsFilterAvailable: PropTypes.bool,
  categoriesFilter: PropTypes.array,
  tripOptionsFilter: PropTypes.array,
  t: PropTypes.func.isRequired,
  couponCode: PropTypes.string,
  discountedTripsByCoupon: PropTypes.array,
  setQueryParams: PropTypes.func,
  isOpenTicketList: PropTypes.bool,
  openTicketService: PropTypes.string,
  setRedirectParams: PropTypes.func,
  hasDynamicPromotion: PropTypes.bool.isRequired,
  isLogged: PropTypes.bool,
  nearTerminals: PropTypes.array,
  providerId: PropTypes.string,
  areOpenTicketTrips: PropTypes.bool,
  features: PropTypes.object.isRequired,
  setTripFilters: PropTypes.func,
  filters: PropTypes.object,
  tripsWithoutFilters: PropTypes.array,
  discountPercent: PropTypes.number,
  operationNumbers: PropTypes.array,
  nit: PropTypes.string,
  goBack: PropTypes.func,
  resultPriceToShow: PropTypes.bool,
  firstTripLineId: PropTypes.string,
  firstTripLineLogo: PropTypes.string,
  showSpecialDiscountBanner: PropTypes.bool,
  specialDiscountVariant: PropTypes.oneOf(['etn', 'gho', 'costa', 'gfa', 'rochoa']),
  brand: PropTypes.string,
  dateFormat: PropTypes.string,
  passengersNotFound: PropTypes.array,
  tripTerminalInfo: PropTypes.object,
  passengersQueryParam: PropTypes.string,
  analyticsSearchData: PropTypes.object,
  resetSeatBus: PropTypes.func,
  fetchTripsDetails: PropTypes.func,
  newPurchase: PropTypes.func,
  departureIsOpenTicket: PropTypes.bool,
  createQuickPurchase: PropTypes.func.isRequired,
  fetchQuickPurchaseByToken: PropTypes.func.isRequired,
  purchaseToken: PropTypes.string.isRequired,
  purchaseLoaded: PropTypes.bool.isRequired,
  purchaseIsFetching: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

/**
 * Represents the ProviderDepartures component.
 */
class ProviderDepartures extends Component {
  /**
   * Constructs a new ProviderDepartures component.
   * @param {object} props - The component props.
   */
  constructor(props) {
    super(props);

    this.state = {
      isQuickPurchaseModalOpen: false,
      quickPurchasePayload: null,
      isQuickPurchaseIntroModalOpen: false,
    };

    this.goToHome = this.goToHome.bind(this);
    this.optionsClick = this.optionsClick.bind(this);
    this.setSortOrder = this.setSortOrder.bind(this);
    this.setActiveFilter = this.setActiveFilter.bind(this);
    this.selectTrip = this.selectTrip.bind(this);
    this.selectTripDefault = this.selectTripDefault.bind(this);
    this.goToProviders = this.goToProviders.bind(this);
    this.selectProvider = this.selectProvider.bind(this);
    this.selectOpenTripList = this.selectOpenTripList.bind(this);
    this.backToNormalTicket = this.backToNormalTicket.bind(this);
    this.selectTripRedirect = this.selectTripRedirect.bind(this);
    this.redirectRoundTrip = this.redirectRoundTrip.bind(this);
    this.setActiveFilters = this.setActiveFilters.bind(this);
    this.initializeQuickPurchaseFromQuery = this.initializeQuickPurchaseFromQuery.bind(this);
    this.handleQuickPurchaseRequest = this.handleQuickPurchaseRequest.bind(this);
    this.handleCloseQuickPurchaseModal = this.handleCloseQuickPurchaseModal.bind(this);
    this.handleQuickPurchaseTokenReady = this.handleQuickPurchaseTokenReady.bind(this);
    this.handleOpenQuickPurchaseIntroModal = this.handleOpenQuickPurchaseIntroModal.bind(this);
    this.handleCloseQuickPurchaseIntroModal = this.handleCloseQuickPurchaseIntroModal.bind(this);
    this.handleCloseFromBackButton = this.handleCloseFromBackButton.bind(this);
    this.isModalHistoryEntry = false;
    this.unlisten = null;
  }

  componentDidMount() {
    const { resetSeatBus, departureIsOpenTicket, openTrip, roundTrip, trips } = this.props;
    $(window).scrollTop(0);
    /*
      FIXME correct the scroll issue with the address bar
    */
    setTimeout(() => $(window).scrollTop(0), 300);

    // Only track if NOT an open ticket list
    // Open tickets from exchange may have unparsed data in Redux from previous search
    // For open ticket lists, tracking happens in componentDidUpdate when data is ready
    const isOpenTicketFlow = trips.some((trip) => trip.openTicket === true);

    if (trips.length && !isOpenTicketFlow) {
      this.trackProductList(this.props);
    }

    cleanAnalyticsParams();
    resetSeatBus();

    // Auto-select open ticket departure if departureIsOpenTicket is true
    if (departureIsOpenTicket && openTrip && roundTrip) {
      this.selectTrip(openTrip, 0, true);
    }

    this.initializeQuickPurchaseFromQuery();

    const { history } = this.props;
    this.unlisten = history.listen((location, action) => {
      if (action === 'POP' && this.isModalHistoryEntry) {
        this.isModalHistoryEntry = false;
        this.handleCloseFromBackButton();
      }
    });
  }

  componentDidUpdate(prevProps) {
    const {
      provider,
      trips,
      departureIsOpenTicket,
      openTrip,
      roundTrip,
      searchIsPolling,
      location,
    } = this.props;

    // Track when trips are added OR when polling finishes
    if (
      provider &&
      (prevProps.trips.length !== trips.length || (prevProps.searchIsPolling && !searchIsPolling))
    ) {
      this.trackProductList();
    }

    // Auto-select open ticket departure if departureIsOpenTicket is true and trips just loaded
    if (
      departureIsOpenTicket &&
      openTrip &&
      roundTrip &&
      prevProps.trips.length === 0 &&
      trips.length > 0
    ) {
      this.selectTrip(openTrip, 0, true);
    }

    if (prevProps.location.search !== location.search) {
      this.initializeQuickPurchaseFromQuery();
    }
  }

  componentWillUnmount() {
    const { resetTripFilters, features } = this.props;
    if (!features.NEW_FILTERS_VERSION) resetTripFilters();
    if (this.unlisten) this.unlisten();
  }

  handleQuickPurchaseRequest(payload) {
    const { history, location } = this.props;

    const queryParams = new URLSearchParams(location.search);
    queryParams.delete('quickPurchaseToken');
    queryParams.delete('qpPassengers');
    queryParams.delete('qpSeats');
    const cleanSearch = queryParams.toString();
    const cleanUrl = `${location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`;

    this.setQuickPurchaseQueryParams({
      quickPurchaseToken: null,
      qpPassengers: null,
      qpSeats: null,
    });

    history.push(cleanUrl);
    this.isModalHistoryEntry = true;

    this.setState({
      isQuickPurchaseModalOpen: true,
      quickPurchasePayload: {
        ...payload,
        passengersParam: payload.passengersParam || 'A1',
        seatNumbers: payload.seatNumbers?.filter(Boolean) || null,
      },
    });
  }

  handleCloseQuickPurchaseModal() {
    if (this.isModalHistoryEntry) {
      const { history } = this.props;
      this.isModalHistoryEntry = false;
      this.setState({ isQuickPurchaseModalOpen: false, quickPurchasePayload: null });
      history.goBack();
    } else {
      this.setState({ isQuickPurchaseModalOpen: false, quickPurchasePayload: null });
      this.setQuickPurchaseQueryParams({
        quickPurchaseToken: null,
        qpPassengers: null,
        qpSeats: null,
      });
    }
  }

  handleCloseFromBackButton() {
    const { purchaseToken, dispatch } = this.props;
    if (purchaseToken) {
      unlockSeats({ purchaseToken, forceUnlock: true, shouldUpdateState: false, dispatch });
    }
    this.setState({ isQuickPurchaseModalOpen: false, quickPurchasePayload: null });
  }

  handleOpenQuickPurchaseIntroModal() {
    this.setState({ isQuickPurchaseIntroModalOpen: true });
  }

  handleCloseQuickPurchaseIntroModal() {
    this.setState({ isQuickPurchaseIntroModalOpen: false });
  }

  handleQuickPurchaseTokenReady(token) {
    const { isQuickPurchaseModalOpen, quickPurchasePayload } = this.state;
    if (!token || !isQuickPurchaseModalOpen || !quickPurchasePayload) return;

    const seatNumbers = quickPurchasePayload?.seatNumbers || [];

    this.setQuickPurchaseQueryParams({
      quickPurchaseToken: token,
      qpPassengers: quickPurchasePayload?.passengersParam || 'A1',
      qpSeats: seatNumbers.length ? seatNumbers.join(',') : null,
    });

    this.setState((prevState) => {
      if (!prevState.isQuickPurchaseModalOpen || !prevState.quickPurchasePayload) return null;
      return {
        quickPurchasePayload: {
          ...prevState.quickPurchasePayload,
          quickPurchaseToken: token,
        },
      };
    });
  }

  getAnalyticsData() {
    const { origin, destination, roundTrip, departureDate, returnDate } = this.props;

    return {
      origin,
      destination,
      roundTrip,
      departureDate: moment(departureDate, 'DD-MMM-YY').toDate(),
      returnDate: moment(returnDate, 'DD-MMM-YY').toDate(),
    };
  }

  setSortOrder(sortBy, list) {
    const { setSortOrder } = this.props;

    setSortOrder(sortBy, list);
    sortTracker(this.getAnalyticsData(), sortBy, 'Departures');
  }

  setActiveFilter(filterBy, active) {
    const { setTripFilter } = this.props;

    setTripFilter(filterBy, active);
    filterTracker(this.getAnalyticsData(), active, 'Departures');
  }

  /**
   * Applied several filters to the search
   * @param {Object} filters - Filters applied
   * @param {*} isFiltersUpdating - Indicates if it is an update of filters but not made by the user
   */
  setActiveFilters(filters, isFiltersUpdating) {
    const { setTripFilters } = this.props;
    setTripFilters(filters);
    if (!isFiltersUpdating) filterTracker(this.getAnalyticsData(), filters, 'Departures');
  }

  setQuickPurchaseQueryParams(params) {
    const { history, location } = this.props;
    const queryParams = new URLSearchParams(location.search);

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        queryParams.delete(key);
      } else {
        queryParams.set(key, value);
      }
    });

    const nextSearch = queryParams.toString();
    history.replace(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`);
  }

  backToNormalTicket() {
    const { currentPath, transitionTo } = this.props;
    const route = currentPath.split('/');

    route.pop();

    transitionTo(route.join('/'));
  }

  trackProductList() {
    const { transportType, trips, provider, passengers } = this.props;

    // Check if provider is undefined
    if (provider === undefined) {
      console.error('Provider is undefined');
      return;
    }

    const { name } = provider;

    productListTracker(transportType, trips, name, decodePassengers(passengers), 'departure');
  }

  goToHome() {
    const { transitionTo } = this.props;
    transitionTo('/');
  }

  optionsClick(option, modalComponent) {
    const { showModal } = this.props;

    showModal(modalComponent);
    resultsActionsTracker(this.getAnalyticsData(), option, 'Departures');
  }

  redirectRoundTrip(trip) {
    const {
      origin,
      destination,
      departureDate,
      returnDate,
      provider,
      passengers,
      transitionTo,
      features,
    } = this.props;

    let route =
      `/search/${origin.id}/${destination.id}/${departureDate}/${returnDate}` +
      `/p/${passengers}/${trip.id}/return/providers`;
    if (!features.PROVIDERS_SELECTION_ENABLED) {
      route =
        `/search/${origin.id}/${destination.id}/${departureDate}/${returnDate}` +
        `/p/${passengers}/${trip.id}/returns/${provider.id}`;
    }
    const recommendedPayload = getRecommendedRedirectPayload({ trip });
    transitionTo(route, {
      seenPrice: trip.pricing.total,
      departs: trip.id,
      ...(recommendedPayload && { tripsInfo: recommendedPayload }),
    });
  }

  selectTripDefault(trip, position, isOpenTicket = false) {
    const { roundTrip, passengers, transitionTo, operationNumbers, nit } = this.props;
    selectedTripTracker(trip, position, 'departure', isOpenTicket);
    if (roundTrip) {
      this.redirectRoundTrip(trip);
    } else {
      const isExchange = operationNumbers?.length && nit;
      const recommendedPayload = getRecommendedRedirectPayload({ trip });
      transitionTo(`/purchase/${trip.id}/new/${passengers}`, {
        seenPrice: trip.pricing.total,
        exchangeData: isExchange
          ? JSON.stringify({
              operationNumbers,
              nit,
            })
          : '',
        ...(recommendedPayload && { tripsInfo: recommendedPayload }),
      });
    }
  }

  selectTripRedirect(trip) {
    const { roundTrip, setRedirectParams, passengersQueryParam, newPurchase } = this.props;
    const { redirectTo, departure, originId, destinationId, lineId, id } = trip;
    const { brand } = redirectTo;
    const selectedSeats =
      isSeatsOnResultActivated() &&
      getSeatsPurchasePayload({
        departureId: id,
      });

    const params = {
      departs_origin: originId,
      departs_destination: destinationId,
      departs_date: moment(departure).format('DD-MM-YYYY'),
      departs_hour: moment(departure).format('HH'),
      departs_minutes: moment(departure).format('mm'),
      departs_line: lineId,
      passengers: passengersQueryParam,
      selected_seats: selectedSeats && JSON.stringify(selectedSeats),
    };
    const shouldRedirectNow = !roundTrip;
    let waitForPurchase = false;

    if (roundTrip) {
      this.redirectRoundTrip(trip);
    } else {
      waitForPurchase = true;
      newPurchase(trip.id, trip.pricing.total);
    }
    setRedirectParams('departure', params, brand, shouldRedirectNow, roundTrip, waitForPurchase);
  }

  selectTrip(trip, position, isOpenTicket = false) {
    const { features } = this.props;
    const { redirectTo, recommendationType } = trip;
    if (recommendationType) {
      trackRecommendedTripSelected({ way: 'departure', recommendationType });
    }

    if (features.TRIP_REDIRECTION_LEAD && redirectTo) {
      this.selectTripRedirect(trip);
    } else {
      this.selectTripDefault(trip, position, isOpenTicket);
    }
  }

  selectOpenTripList() {
    const { passengers, transitionTo, origin, destination, departureDate, returnDate, roundTrip } =
      this.props;

    const route =
      `/search/${origin.id}/${destination.id}/${departureDate}` +
      `${roundTrip ? `/${returnDate}` : ''}` +
      `/p/${passengers}/departures/open`;

    transitionTo(route);
  }

  goToProviders() {
    const { currentPath, setTripFilter, transitionTo } = this.props;
    const route = currentPath.split('/');

    // Remove current provider id from currentPath array and add 'provider' to go
    // to return providers route
    route.pop();
    route.push('providers');

    // Reset location filters
    setTripFilter('departureLocation', 'none');
    setTripFilter('arrivalLocation', 'none');

    transitionTo(route.join('/'));
  }

  selectProvider(provider, ignoreHistory = false) {
    const {
      origin,
      destination,
      departureDate,
      returnDate,
      currentPath,
      setTripFilter,
      transitionTo,
      replaceUrl,
    } = this.props;
    let route = currentPath.split('/');

    pickedProviderTracker(
      {
        origin,
        destination,
        roundTrip: true,
        departureDate: moment(departureDate, 'DD-MMM-YY').toDate(),
        returnDate: moment(returnDate, 'DD-MMM-YY').toDate(),
      },
      provider,
    );

    // Remove current provider from currentPath array and add new provider id
    route.pop();
    route.push(provider.id);
    route = route.join('/');

    // Reset location filters
    setTripFilter('departureLocation', 'none');
    setTripFilter('arrivalLocation', 'none');

    if (ignoreHistory) {
      replaceUrl(route);
    } else {
      transitionTo(route);
    }
  }

  hasFiltersApplied() {
    const { filters } = this.props;
    return (
      !filters.departureTime.includes('none') ||
      filters.categories?.length ||
      !filters.stops.includes('none') ||
      filters.tripOptions?.length
    );
  }

  initializeQuickPurchaseFromQuery() {
    const { location, fetchQuickPurchaseByToken, purchaseToken, purchaseLoaded } = this.props;
    const { quickPurchaseToken, qpPassengers, qpSeats } = parseQueryString(location.search);

    if (!quickPurchaseToken || !qpPassengers) return;

    const seatNumbers = qpSeats ? qpSeats.split(',').filter(Boolean) : null;

    this.setState((prevState) => {
      const sameToken = prevState.quickPurchasePayload?.quickPurchaseToken === quickPurchaseToken;

      if (prevState.isQuickPurchaseModalOpen && sameToken) {
        return null;
      }

      return {
        isQuickPurchaseModalOpen: true,
        quickPurchasePayload: {
          passengersParam: qpPassengers,
          seatNumbers,
          quickPurchaseToken,
        },
      };
    });

    if (purchaseToken !== quickPurchaseToken || !purchaseLoaded) {
      fetchQuickPurchaseByToken(quickPurchaseToken).catch(() => {
        this.handleCloseQuickPurchaseModal();
      });
    }
  }

  renderTripList(openTicket = null, isExchange = false) {
    const {
      isOpenTicketList,
      roundTrip,
      hasDynamicPromotion,
      isLogged,
      providerId,
      features,
      filters,
      tripsWithoutFilters,
      passengersNotFound,
      couponCode,
      discountedTripsByCoupon,
      trips,
      promoUrgencyTrips,
      closedTrips,
      origin,
      destination,
      showSpecialDiscountBanner,
      specialDiscountVariant,
      analyticsSearchData,
      searchIsPolling,
      departureDate,
    } = this.props;

    const showOpenTicket =
      !this.hasFiltersApplied() || (this.hasFiltersApplied() && !tripsWithoutFilters.length);

    return isOpenTicketList ? (
      <OpenTicketList
        way="departure"
        roundTrip={roundTrip}
        selectTrip={this.selectTrip}
        providerId={providerId}
        isExchange={isExchange}
      />
    ) : (
      <>
        {features.OPEN_TICKET_MODAL_ENABLED && <ModalOpenTicket selectTrip={this.selectTrip} />}
        {features.SHOW_DYNAMIC_PROMOTIONS && hasDynamicPromotion && (
          <ModalDiscount goToAnticipationSearch={this.goToAnticipationSearch} />
        )}
        <Spacing responsiveSize="S" vertical>
          <DiscountUITracker
            trips={promoUrgencyTrips}
            roundTrip={roundTrip}
            way="departure"
            analyticsSearchData={analyticsSearchData}
            searchIsPolling={searchIsPolling}
            searchDate={departureDate}
            scopeId={providerId}
          />
          <PromoUrgencyBannerContainer
            trips={promoUrgencyTrips}
            roundTrip={roundTrip}
            way="departure"
            analyticsSearchData={analyticsSearchData}
            searchIsPolling={searchIsPolling}
            departureDate={departureDate}
          />
          <div>
            <RecommendedTripsList
              couponCode={couponCode}
              discountedTripsByCoupon={discountedTripsByCoupon}
              way="departure"
              selectTrip={this.selectTrip}
              onOptionClick={this.optionsClick}
              onQuickPurchaseRequest={this.handleQuickPurchaseRequest}
              onBannerClick={this.handleOpenQuickPurchaseIntroModal}
              providerId={providerId}
              filtersApplied={filters}
              searchTrips={trips}
              isRoundTrip={roundTrip}
              isExchange={Boolean(isExchange)}
              originCityName={origin?.cityName}
              destinationCityName={destination?.cityName}
              showSpecialDiscountBanner={showSpecialDiscountBanner}
              specialDiscountVariant={specialDiscountVariant}
            />
          </div>
          <TripList
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...this.props}
            way="departure"
            selectTrip={this.selectTrip}
            onOptionClick={this.optionsClick}
            tripOpenTicket={showOpenTicket && openTicket}
            isLogged={isLogged}
            filtersApplied={filters}
            showPoweredBy={
              !tripsWithoutFilters.length && (!closedTrips || closedTrips.length === 0)
            }
            showDotersBanner={!tripsWithoutFilters.length && !isLogged && features.DOTERS_ENABLED}
            passengersNotFound={passengersNotFound}
            onQuickPurchaseRequest={this.handleQuickPurchaseRequest}
          />
        </Spacing>
      </>
    );
  }

  renderTripListWithoutFilters(openTicket = null) {
    const { tripsWithoutFilters, isLogged, filters, features } = this.props;
    return (
      <TripList
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...this.props}
        trips={tripsWithoutFilters}
        way="departure"
        selectTrip={this.selectTrip}
        onOptionClick={this.optionsClick}
        tripOpenTicket={openTicket}
        isLogged={isLogged}
        filtersApplied={filters}
        isNotFiltered
        showDotersBanner={tripsWithoutFilters.length && !isLogged && features.DOTERS_ENABLED}
        onQuickPurchaseRequest={this.handleQuickPurchaseRequest}
      />
    );
  }

  render() {
    const {
      provider,
      providers,
      noTrips,
      tooManyFilters,
      searchIsPolling,
      showModal,
      sortBy,
      sortProvidersBy,
      departureFilter,
      stopsFilter,
      categoriesFilter,
      tripOptionsFilter,
      departureLocationFilter,
      arrivalLocationFilter,
      locations,
      activeLocationFilter,
      activeDepartureFilter,
      activeStopsFilter,
      tripDetails,
      closeTripDetails,
      openTrip,
      roundTrip,
      trips,
      isStopsFilterAvailable,
      t,
      isOpenTicketList,
      nearTerminals,
      providerId,
      areOpenTicketTrips,
      features,
      departureDate,
      resetTripFilters,
      tripsWithoutFilters,
      operationNumbers,
      nit,
      goBack,
      discountPercent,
      resultPriceToShow,
      firstTripLineId,
      firstTripLineLogo,
      brand,
      dateFormat,
      tripTerminalInfo,
      closedTrips,
      isLogged,
      filters,
      origin,
      destination,
      createQuickPurchase,
      purchaseToken,
      purchaseLoaded,
      purchaseIsFetching,
      history,
      location,
      passengers,
    } = this.props;
    const { isQuickPurchaseModalOpen, quickPurchasePayload, isQuickPurchaseIntroModalOpen } =
      this.state;
    const hasAvailableTrips = trips.length > 0;
    const hasResults = isOpenTicketList || !noTrips || (noTrips && tooManyFilters);
    const isExchange = operationNumbers?.length && nit;
    const selectedCategory =
      getSelectedPassengerCategory(passengers) ||
      categoriesFilter?.[0] ||
      categoriesFilter?.get?.(0);
    const isBenefitCategory =
      features.FREE_FARE_BENEFIT_DENIAL &&
      selectedCategory &&
      !GENERAL_PASSENGER_TYPES.includes(selectedCategory);
    /**
     * Check whether the trip has availability for the selected benefit category.
     * @param {object} trip - Trip from search results.
     * @returns {boolean} True when the category has seats available.
     */
    const tripHasCategoryAvailability = (trip) =>
      trip.passengerTypes?.some((pt) => pt.type === selectedCategory && pt.availability > 0);
    const categoryHasAvailability =
      searchIsPolling ||
      !isBenefitCategory ||
      (!noTrips && trips.some(tripHasCategoryAvailability));
    // When the full gratuity (e.g. idoso 100% = older) is sold out, check if the half-fare
    // equivalent (idoso 50% = older_half) is available. The booking already falls back to it
    // (see getPassengerCategory), so the denial modal can offer buying at 50% or issuing the denial.
    const halfFareCategory = HALF_FARE_BY_CATEGORY[selectedCategory];
    const tripHasHalfFareAvailability = (trip) =>
      trip.passengerTypes?.some((pt) => pt.type === halfFareCategory && pt.availability > 0);
    const halfFareAvailable =
      isBenefitCategory &&
      !categoryHasAvailability &&
      Boolean(halfFareCategory) &&
      !noTrips &&
      trips.some(tripHasHalfFareAvailability);

    const departureMoment = getMomentFromSearchDateParam(departureDate);
    const departureDateFormat = normalizeSearchWidgetDateFormat(dateFormat, 'DD-MMMM-YY');

    const fullMonth = departureMoment.format('MMMM');

    const departureDateText = departureMoment
      .format(departureDateFormat)
      .replace(fullMonth, fullMonth.slice(0, 3));

    const showFullLoadingScreen =
      searchIsPolling && (noTrips || features.HIDE_CACHED_TRIPS_DURING_POLLING);

    const scheduleTitle = isOpenTicketList && 'openDeparture';
    const showNearbyTerminals =
      features.USE_NERBY_TERMINALS && nearTerminals?.length > 0 && !isExchange;

    let openTicketCTA = (
      <OpenTicketCTA
        openTrip={openTrip}
        way="departure"
        isRoundTrip={roundTrip}
        showWalletPoints={resultPriceToShow.priceType === 'wallet'}
        walletType={resultPriceToShow.walletType}
        onSelect={
          features.OPEN_TICKET_PROVIDER_SELECTION_ENABLED
            ? this.selectOpenTripList
            : this.selectTrip
        }
      />
    );

    if (isOpenTicketList && openTrip) {
      openTicketCTA = (
        <NormalTicketWrapper>
          <NormalTicketButton
            way="departure"
            onClick={isExchange ? goBack : this.goToProviders}
            isExchange={isExchange}
          />
        </NormalTicketWrapper>
      );
    }

    let TerminalsComponent;
    if (showNearbyTerminals) {
      TerminalsComponent = <NerbyTerminals onColumns />;
    }

    const showDaysControl = (!isOpenTicketList || areOpenTicketTrips) && !isExchange;

    const activeFiltersFunction = features.NEW_FILTERS_VERSION
      ? this.setActiveFilters
      : this.setActiveFilter;

    const hasExtraResultLines = features.EXTRA_RESULT_LINES?.includes(firstTripLineId);
    const showDiscountBanner = features.SHOW_DISCOUNT_BANNER && !hasExtraResultLines;
    const showBanners = hasExtraResultLines || showDiscountBanner;

    const isNewResultsDesign = features.NEW_RESULTS_DESIGN;

    const isSeatsOnResults = isSeatsOnResultActivated();

    const useSearchSidebar =
      (brand === 'berlinas' && isNewResultsDesign) || isSeatsOnResults
        ? false
        : features.SEARCH_SIDEBAR_ENABLED;

    const showRouteTerminalsDisplay = brand !== 'reservamos';

    const containerClassName = `departures-container ${
      !isSeatsOnResults && (useSearchSidebar || isNewResultsDesign) ? 'with-sidebar' : ''
    }`;

    return (
      <>
        {!categoryHasAvailability && (
          <BenefitDenialModal
            origin={origin}
            destination={destination}
            date={departureDate}
            category={selectedCategory}
            halfFareAvailable={halfFareAvailable}
          />
        )}
        {showNearbyTerminals && (
          <Visibility type="hideForMedium">
            <WhiteBar>{TerminalsComponent}</WhiteBar>
          </Visibility>
        )}
        <DaysCarousel way="departure" containerClassName={containerClassName} />
        {showFullLoadingScreen ? <LoadingScreen resultMessages hasHeader={false} /> : null}
        {hasResults ? (
          <QuickPurchaseExperimentGate
            trips={trips}
            isRoundTrip={roundTrip}
            isExchange={Boolean(isExchange)}
            isSearchPolling={Boolean(searchIsPolling)}
          >
            <div className={containerClassName}>
              {!useSearchSidebar && (
                <>
                  <Spacing vertical size="L">
                    <TripListHeader
                      way="departure"
                      provider={provider}
                      hasOtherProviders={providers.length > 1}
                      sortOn={!!sortBy}
                      departureFilterOn={activeDepartureFilter}
                      locationFilterOn={activeLocationFilter}
                      stopsFilterOn={activeStopsFilter}
                      onOptionClick={this.optionsClick}
                      showLocationFilter={
                        locations.departure.length > 2 || locations.arrival.length > 2
                      }
                      locations={locations}
                      floatingOptions={
                        2 + (locations.departure.length > 2 || locations.arrival.length > 2)
                      }
                      sortProperty={sortBy}
                      providerSortProperty={sortProvidersBy}
                      activeDeparture={departureFilter}
                      departureLocation={departureLocationFilter}
                      arrivalLocation={arrivalLocationFilter}
                      onSortSelect={this.setSortOrder}
                      onFilterSelect={activeFiltersFunction}
                      showModal={showModal}
                      onChangeProviderClick={this.goToProviders}
                      stopsFilter={stopsFilter}
                      trips={trips}
                      date={departureDateText}
                      isStopsFilterAvailable={isStopsFilterAvailable}
                      roundTrip={roundTrip}
                      isOpenTicketList={isOpenTicketList}
                      onResetFilters={resetTripFilters}
                    />
                  </Spacing>
                  {isNewResultsDesign && !isExchange && showBanners && (
                    <Spacing
                      justifyContent={hasExtraResultLines ? 'space-between' : 'flex-end'}
                      size="S"
                    >
                      <When condition={hasExtraResultLines}>
                        <LineBanner brand={firstTripLineId} logoUrl={firstTripLineLogo} />
                      </When>
                      <When condition={showDiscountBanner}>
                        <DiscountBanner discountPercent={discountPercent} way="departure" />
                      </When>
                    </Spacing>
                  )}
                </>
              )}

              {useSearchSidebar && (
                <>
                  <div className="sidebar-title">
                    {features.USE_HORIZONTAL_RESULTS && (
                      <p className="sidebar-title-copy">
                        {t(
                          'search:trip_discount_title',
                          features.SHOW_CAPACITY_CATEGORIES ? { context: 'antt' } : {},
                        )}
                      </p>
                    )}
                  </div>
                  <SearchSidebar
                    onChangeSort={this.setSortOrder}
                    onChangeScheduleFilter={activeFiltersFunction}
                    scheduleFilter={departureFilter}
                    categoriesFilter={categoriesFilter}
                    tripOptionsFilter={tripOptionsFilter}
                    isStopsFilterAvailable={isStopsFilterAvailable}
                    stopsFilter={stopsFilter}
                    sort={sortBy}
                    way="departure"
                    hasOtherProviders={providers.length > 1}
                    onChangeProviderClick={this.goToProviders}
                    onResetFilters={resetTripFilters}
                    isOpenTicketList={isOpenTicketList}
                    isPolling={searchIsPolling}
                  />
                  <ResultsTitle
                    sectionTitle={scheduleTitle}
                    date={!isOpenTicketList && departureDateText}
                    rightContent={
                      isNewResultsDesign && !isExchange ? <SwitchDoters /> : openTicketCTA
                    }
                  />
                </>
              )}

              <div className="results-container">
                <Spacing vertical size="L">
                  {Boolean(searchIsPolling) && <LabelLoading />}
                  {hasResults || isOpenTicketList
                    ? this.renderTripList(openTicketCTA, isExchange)
                    : null}
                  {features.NEW_FILTERS_VERSION &&
                  tripsWithoutFilters.length &&
                  hasResults &&
                  !isOpenTicketList ? (
                    <>
                      <Text weight="bold" size="L" mobileSize="M">
                        {t('search:label.other_trips_you_may_be_interested_on')}
                      </Text>
                      {this.renderTripListWithoutFilters(openTicketCTA)}
                    </>
                  ) : null}

                  {showRouteTerminalsDisplay && tripTerminalInfo.hasCoords && (
                    <RouteTerminalsDisplay
                      terminalOrigin={tripTerminalInfo.originTerminal}
                      cityOrigin={tripTerminalInfo.originCity}
                      terminalDestination={tripTerminalInfo.destinationTerminal}
                      cityDestination={tripTerminalInfo.destinationCity}
                      originUrl={tripTerminalInfo.originMapUrl}
                      destinationUrl={tripTerminalInfo.destinationMapUrl}
                    />
                  )}

                  <ClosedTripsSection
                    closedTrips={closedTrips}
                    selectTrip={this.selectTrip}
                    isRoundTrip={roundTrip}
                    way="departure"
                    isLogged={isLogged}
                    filtersApplied={filters}
                    features={features}
                    origin={origin}
                    destination={destination}
                  />

                  <Spacing vertical size="S">
                    <div className="results-bottom-container fade-in">
                      {Boolean(!searchIsPolling && hasAvailableTrips) && (
                        <Text size="S" color="grayLight">
                          <em>{t('general:showing_all_available_trips')}</em>
                        </Text>
                      )}
                      <SwitchDoters />
                    </div>

                    <div className="results-bottom-container fade-in">
                      {features.COUPON_ON_SEARCH && !isExchange && <Coupon />}

                      {!isOpenTicketList && features.PROVIDERS_SELECTION_ENABLED && (
                        <ChangeProvider
                          hasOtherProviders={providers.length > 1}
                          onChangeProviderClick={this.goToProviders}
                        />
                      )}
                    </div>
                  </Spacing>

                  {showNearbyTerminals && (
                    <Visibility type="hideForLargeOnly">{TerminalsComponent}</Visibility>
                  )}
                  {features.SHOW_MIN_PRICES && showDaysControl && !isExchange && (
                    <DayControls fixed way="departure" providerId={providerId} />
                  )}
                  {features.SHOW_POWERED_BY && <PoweredBy />}
                </Spacing>
              </div>
              <OverlayView
                title="trip_details"
                visible={Boolean(tripDetails)}
                hideOverlay={closeTripDetails}
              >
                <TripDetails details={tripDetails} />
              </OverlayView>
            </div>
          </QuickPurchaseExperimentGate>
        ) : (
          <>
            <NoResults way="departure" providerId={providerId} />
            <div className="departures-container">
              <div className="results-container">
                <Spacing vertical size="L">
                  <ClosedTripsSection
                    closedTrips={closedTrips}
                    selectTrip={this.selectTrip}
                    isRoundTrip={roundTrip}
                    way="departure"
                    isLogged={isLogged}
                    filtersApplied={filters}
                    features={features}
                    origin={origin}
                    destination={destination}
                  />

                  {closedTrips && closedTrips.length > 0 && (
                    <Spacing vertical size="S">
                      <div className="results-bottom-container fade-in">
                        {Boolean(!searchIsPolling) && (
                          <Text size="S" color="grayLight">
                            <em>{t('general:showing_all_available_trips')}</em>
                          </Text>
                        )}
                      </div>
                      {features.SHOW_POWERED_BY && <PoweredBy />}
                    </Spacing>
                  )}
                </Spacing>
              </div>
            </div>
          </>
        )}

        <QuickPurchaseModal
          isOpen={isQuickPurchaseModalOpen}
          payload={quickPurchasePayload}
          onClose={this.handleCloseQuickPurchaseModal}
          createQuickPurchase={createQuickPurchase}
          purchaseToken={purchaseToken}
          purchaseLoaded={purchaseLoaded}
          purchaseIsFetching={purchaseIsFetching}
          onTokenReady={this.handleQuickPurchaseTokenReady}
          history={history}
          location={location}
        />
        <QuickPurchaseIntroModal
          trips={trips}
          isRoundTrip={roundTrip}
          isExchange={Boolean(isExchange)}
          isQuickPurchaseModalOpen={isQuickPurchaseModalOpen}
          isOpen={isQuickPurchaseIntroModalOpen}
          onClose={this.handleCloseQuickPurchaseIntroModal}
        />
      </>
    );
  }
}

ProviderDepartures.propTypes = propTypes;

const mapStateToProps = (state) => ({
  features: state.whitelabelConfig.features,
});

export default connect(mapStateToProps)(ProviderDepartures);
